package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.AppTypography
import com.arenaproplay.admin.ui.theme.SportColorScheme
import com.arenaproplay.admin.ui.theme.SportColors
import com.arenaproplay.admin.ui.utils.*
import org.jetbrains.compose.web.css.*

/**
 * Размеры карточки игрока (дизайн-сетка).
 * Пиксели соответствуют Figma — admin пинует --scale к 1px, s() = plain px.
 */
enum class PlayerCardSize(val w: Int, val h: Int) {
    Large(254, 385),
    Medium(130, 202),
    Small(130, 204),
    Arena(94, 147),
    XtraSmall(58, 90),
    Highlight(37, 58)
}

private const val CDN_BASE = "https://images.arenaproplay.com/"

/**
 * Мягкий outline вокруг имени игрока — синяя подсветка у края букв.
 */
private fun Modifier.cardTextOutline(): Modifier = styleModifier {
    property(
        "text-shadow",
        "-1px -1px 0 rgba(0, 97, 255, 0.05), " +
            "1px -1px 0 rgba(0, 97, 255, 0.05), " +
            "-1px 1px 0 rgba(0, 97, 255, 0.05), " +
            "1px 1px 0 rgba(0, 97, 255, 0.05), " +
            "0 0 2px rgba(0, 97, 255, 0.05)"
    )
}

/**
 * Настоящая карточка игрока — 4-слойная композиция:
 *   Layer 1: фон-фрейм (card.png / cardImageUrl)
 *   Layer 2: аватар с CSS-маской по силуэту карточки
 *   Layer 3: блок рейтинга (скрыт при hideRating=true)
 *   Layer 4: блок имени
 *
 * Порт web-репо Pro-play-arena-web/PlayerCard.kt для admin-панели.
 * --scale в admin закреплён на 1px, поэтому s() = plain px.
 *
 * @param playerName   Имя игрока (AdminPerson.name)
 * @param sport        Спорт для цветовой схемы (из interestSports[0] или "football")
 * @param size         Размер карточки (рекомендуется Medium для грида)
 * @param avatarUrl    AI-сгенерированный аватар (profile_cards.avatar_url)
 * @param cardAssetsBasePath  CDN-путь до ассетов шаблона (card_templates.assets_base_path)
 * @param cardTextColor       Hex-цвет текста шаблона (card_templates.text_color)
 * @param cardFrameUrl        Прямой URL фрейма-фоллбэка (card_templates.image_url)
 * @param hideRating   Скрыть блок рейтинга (рекомендуется true для admin)
 * @param fillContainer Растянуть карточку до размеров родителя через CSS scale
 * @param modifier     Дополнительные модификаторы
 */
@Composable
fun PlayerCard(
    playerName: String = "",
    sport: String = "football",
    size: PlayerCardSize = PlayerCardSize.Medium,
    avatarUrl: String? = null,
    cardAssetsBasePath: String? = null,
    cardTextColor: String? = null,
    cardFrameUrl: String = "",
    hideRating: Boolean = true,
    fillContainer: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val sc = SportColors.forSport(sport)

    val card = @Composable {
        AdminImageCardFrame(
            size = size,
            sc = sc,
            playerName = playerName,
            cardFrameUrl = cardFrameUrl,
            avatarUrl = avatarUrl,
            cardAssetsBasePath = cardAssetsBasePath,
            cardTextColor = cardTextColor,
            hideRating = hideRating,
        )
    }

    if (fillContainer) {
        // Внешний Box — контейнер-query. Внутренний — дизайн-сетка (size.w × size.h),
        // масштабируется через transform: scale(...) чтобы заполнить слот.
        Box(
            modifier = modifier
                .fillMaxSize()
                .styleModifier {
                    property("container-type", "size")
                    property("position", "relative")
                    property("overflow", "visible")
                }
        ) {
            Box(
                modifier = Modifier
                    .sWidth(size.w)
                    .sHeight(size.h)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "50%")
                        property("top", "0")
                        property(
                            "transform",
                            "translateX(-50%) scale(min(calc(100cqw / ${size.w}px), calc(100cqh / ${size.h}px)))"
                        )
                        property("transform-origin", "top center")
                    }
            ) { card() }
        }
    } else {
        Box(modifier) { card() }
    }
}

// =============================================================================
// Внутренний фрейм карточки (4 слоя)
// =============================================================================

@Composable
private fun AdminImageCardFrame(
    size: PlayerCardSize,
    sc: SportColorScheme,
    playerName: String,
    cardFrameUrl: String,
    avatarUrl: String?,
    cardAssetsBasePath: String?,
    cardTextColor: String?,
    hideRating: Boolean,
) {
    val hasAssets = !cardAssetsBasePath.isNullOrBlank()
    // Layer 1: URL фона (CDN-ассет или прямой URL фоллбэка)
    val bgUrl = if (hasAssets) "${CDN_BASE}${cardAssetsBasePath}/card.png" else cardFrameUrl

    // Применить цвет текста шаблона поверх спортивной схемы
    val effectiveSc = if (!cardTextColor.isNullOrBlank()) {
        val hex = cardTextColor.removePrefix("#")
        sc.copy(
            primary = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
                hex.substring(0, 2).toInt(16),
                hex.substring(2, 4).toInt(16),
                hex.substring(4, 6).toInt(16)
            )
        )
    } else sc

    // Layer 1 — фон-фрейм
    Box(
        Modifier
            .sWidth(size.w)
            .sHeight(size.h)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("position", "relative")
                property("background", "url('${bgUrl}') center/cover no-repeat")
                property("background-size", "100% 100%")
            }
    ) {
        // Layer 2 — аватар с CSS-маской
        if (avatarUrl != null) {
            AdminAvatarOverlay(
                avatarUrl = avatarUrl,
                maskUrl = bgUrl.takeIf { it.isNotBlank() }
            )
        }

        // Layer 3 — блок рейтинга (скрыт в admin)
        if (hasAssets && !hideRating && size != PlayerCardSize.Highlight) {
            AdminAssetRatingContainer(cardAssetsBasePath = cardAssetsBasePath!!, sc = effectiveSc)
        }

        // Layer 4 — блок имени
        if (playerName.isNotBlank() && size != PlayerCardSize.Highlight) {
            AdminPlayerInfoBlock(size = size, sc = effectiveSc, playerName = playerName)
        }
    }
}

// =============================================================================
// Layer 2: Аватар поверх фрейма с CSS-маской по силуэту card.png
// =============================================================================

@Composable
private fun AdminAvatarOverlay(avatarUrl: String, maskUrl: String?) {
    val isDefault = avatarUrl.contains("ic_empty_avatar")
    Image(
        src = avatarUrl,
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                property("top", "0")
                property("left", "0")
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "cover")
                property("object-position", "center 55%")
                property("transform", "scale(0.87)")
                property("transform-origin", "center 55%")
                property("pointer-events", "none")
                property("will-change", "transform")
                property("backface-visibility", "hidden")
                if (!isDefault) property("filter", "contrast(1.1) brightness(1.02)")
                if (maskUrl != null) {
                    property("-webkit-mask-image", "url('$maskUrl')")
                    property("-webkit-mask-size", "100% 100%")
                    property("-webkit-mask-repeat", "no-repeat")
                    property("-webkit-mask-position", "center")
                    property("mask-image", "url('$maskUrl')")
                    property("mask-size", "100% 100%")
                    property("mask-repeat", "no-repeat")
                    property("mask-position", "center")
                }
            }
    )
}

// =============================================================================
// Layer 3: Контейнер рейтинга — card_rating.png
// Figma: Container_Rating at (18, 58) 66×100 на 238×369 card
// В admin всегда hideRating=true, оставлен для полноты анатомии.
// =============================================================================

@Composable
private fun AdminAssetRatingContainer(
    cardAssetsBasePath: String,
    sc: SportColorScheme,
) {
    Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", "9.56%")   // 18/238
            property("top", "16.72%")   // 58/369
            property("width", "15.27%") // 66/238 × 0.55 (нет рейтинга/эмблем)
            property("height", "14.91%")// 100/369 × 0.55
            property("overflow", "visible")
        },
        contentAlignment = Alignment.Center
    ) {
        Image(
            src = "${CDN_BASE}${cardAssetsBasePath}/card_rating.png",
            modifier = Modifier.fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("pointer-events", "none")
                }
        )
    }
}

// =============================================================================
// Layer 4: Блок имени (центр-низ карточки)
// =============================================================================

@Composable
private fun AdminPlayerInfoBlock(
    size: PlayerCardSize,
    sc: SportColorScheme,
    playerName: String,
) {
    val left: String
    val top: String
    val blockWidth: String
    val nameFS: Int
    val nameLH: Int

    when (size) {
        PlayerCardSize.Large -> {
            left = s(56); top = s(245); blockWidth = s(142)
            nameFS = 22; nameLH = 26
        }
        PlayerCardSize.Medium, PlayerCardSize.Small -> {
            left = s(26); top = s(122); blockWidth = s(78)
            nameFS = 12; nameLH = 12
        }
        PlayerCardSize.Arena -> {
            left = s(19); top = s(86); blockWidth = s(56)
            nameFS = 8; nameLH = 14
        }
        PlayerCardSize.XtraSmall -> {
            left = s(12); top = s(71); blockWidth = s(35)
            nameFS = 6; nameLH = 5
        }
        else -> return
    }

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", left)
            property("top", top)
            property("width", blockWidth)
            property("will-change", "transform")
        }
    ) {
        SpanText(
            playerName,
            modifier = Modifier
                .fontFamily(
                    // Как в приложении: SF Pro Display (глобальный CARD_HEADLINE_FONT
                    // админки — Unbounded — здесь не подходит, имя выпирает из карточки)
                    "SF Pro Display",
                    *AppTypography.CARD_HEADLINE_FALLBACKS
                )
                .fontWeight(AppTypography.Bold)
                .sFontSize(nameFS)
                .sLineHeight(nameLH)
                .color(sc.primary)
                .cardTextOutline()
                .textAlign(TextAlign.Center)
                .fillMaxWidth()
                .styleModifier {
                    property("text-transform", "uppercase")
                    property(
                        "letter-spacing",
                        if (size == PlayerCardSize.Large) s(0.22) else s(0.012)
                    )
                }
        )
    }
}
